import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: boolean;
}

export default function OtpInput({
  length = 4,
  value,
  onChange,
  onComplete,
  error = false,
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.split("").concat(Array(length).fill("")).slice(0, length);

  const commit = (next: string) => {
    const trimmed = next.slice(0, length);
    onChange(trimmed);
    if (trimmed.length === length) {
      onComplete?.(trimmed);
    }
  };

  const handleChange = (index: number, raw: string) => {
    const clean = raw.replace(/\D/g, "");
    if (!clean) return;
    const arr = value.split("");
    for (let i = 0; i < clean.length && index + i < length; i += 1) {
      arr[index + i] = clean[i];
    }
    const next = arr.join("").slice(0, length);
    commit(next);
    const focusAt = Math.min(index + clean.length, length - 1);
    refs.current[focusAt]?.focus();
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = value.split("");
      if (arr[index]) {
        arr[index] = "";
        onChange(arr.join("").slice(0, length));
      } else if (index > 0) {
        arr[index - 1] = "";
        onChange(arr.join("").slice(0, length));
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowRight" && index < length - 1) {
      refs.current[index + 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    commit(pasted);
    refs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div dir="ltr" className="flex items-center justify-center gap-3">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={`w-14 h-16 text-center text-2xl font-bold rounded-btn border-2 outline-none transition-all ${
            error
              ? "border-red-300 bg-red-50 text-red-600"
              : digits[index]
                ? "border-primary-500 bg-primary-50 text-foreground-900"
                : "border-background-300 bg-background-50 text-foreground-900"
          } focus:border-primary-500 focus:ring-2 focus:ring-primary-200`}
        />
      ))}
    </div>
  );
}